import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LoaderCircle,
  Menu,
  Search,
} from 'lucide-react';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { pageCopy } from '@shared/lib/pageCopy';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useUiStore } from '@shared/stores/uiStore';
import { useQuickLinksStore } from '@shared/stores/quickLinksStore';
import { useVisibleTiles, useVisibleCategories } from '@features/dashboard/useVisibleTiles';
import type { CompiledPublicTile } from '@shared/types/domainConfig';
import { cn } from '@shared/lib/cn';
import { isPanicTile, runTileAction } from '@shared/lib/tileActions';
import { QuickLinkIcon } from '@features/dashboard/QuickLinkTile';
import { listHeadingClass } from '@features/dashboard/widgets/WidgetCard';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { ListItem } from '@shared/ui/lists/ListItem';
import { ExploreSearch, type ExploreGroup } from './ExploreSearch';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { domainForResolution, loadDomainConfig } from '@shared/api/domainApi';

const MORE_BACK_STATE = { backTo: '/app/more' } as const;

/**
 * Explore tab — one list of every feature not in the bottom tabs: the main
 * tiles first, then a small heading per group, each row opening its feature
 * in one tap. The search button opens a floating search box. When the tenant's domain-config ships
 * `experienceStore.tiles.more_categories`, those tiles drive the page.
 * API failures should not expose a hardcoded feature catalog; instead we
 * show a loading or unavailable state until the configured experience is
 * known.
 */

export function MorePage(): JSX.Element {
  const experience = useExperienceStore((s) => s.experience);
  const loadState = useExperienceStore((s) => s.loadState);
  const allCategories = useExperienceStore((s) => s.getMoreCategories());
  const navigate = useNavigate();
  const copy = pageCopy().more;
  // Same permission gate as the home grid — tiles the member's role can't open
  // (and any category left empty by that) never render.
  const orderedTiles = useMemo(
    () => [...(experience?.tiles?.main ?? [])].sort((a, b) => a.order - b.order),
    [experience],
  );
  const mainTiles = useVisibleTiles(orderedTiles);
  const categories = useVisibleCategories(allCategories);
  const openNavDrawer = useUiStore((s) => s.openNavDrawer);
  const recent = useQuickLinksStore((s) => s.recent);
  const promote = useQuickLinksStore((s) => s.promote);
  const [searchOpen, setSearchOpen] = useState(false);

  const groups = useMemo<ExploreGroup[]>(
    () => [
      { id: 'main', name: '', tiles: mainTiles },
      ...categories.map((cat) => ({
        id: cat.category_id,
        name: cat.name,
        tiles: [...cat.tiles].sort((a, b) => a.order - b.order),
      })),
    ].filter((group) => group.tiles.length > 0),
    [mainTiles, categories],
  );

  // Opening a feature also counts as using it, for search's "Last used".
  const openTile = (tile: CompiledPublicTile) => {
    promote(tile.id);
    runTileAction(tile, navigate, MORE_BACK_STATE);
  };

  // The property title block lives on the home shell; here the centred page title
  // owns the title slot, so the leading cluster is the menu alone.
  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={copy.title}
      showBack={false}
      leading={
        <button
          type="button"
          onClick={openNavDrawer}
          aria-label="Open menu"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur"
        >
          <Menu className="h-4.5 w-4.5" strokeWidth={1.75} />
        </button>
      }
      action={
        groups.length > 0 ? (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur"
          >
            <Search className="h-4.5 w-4.5" strokeWidth={2} />
          </button>
        ) : undefined
      }
    />
  );

  const retryLoad = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const domain = domainForResolution(hostname);
    if (!domain) return;
    void loadDomainConfig(domain).catch((error) => {
      console.warn('[more] domain-config reload failed:', error);
    });
  };

  if (groups.length > 0) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-1 pb-safe-or-6">
          {groups.map((group) => (
            <section key={group.id} className="mt-5">
              {group.name ? <h2 className={cn(listHeadingClass, 'mb-2')}>{group.name}</h2> : null}
              <ListGroup>
                {group.tiles.map((tile) => (
                  <ListItem
                    key={tile.id}
                    title={tile.label}
                    leading={
                      <QuickLinkIcon link={{ label: tile.label, imageUrl: tile.image_url, isPanic: isPanicTile(tile) }} />
                    }
                    onClick={() => openTile(tile)}
                  />
                ))}
              </ListGroup>
            </section>
          ))}
        </div>

        <ExploreSearch
          open={searchOpen}
          onOpenChange={setSearchOpen}
          groups={groups}
          recent={recent}
          onSelect={openTile}
        />
      </div>
    );
  }

  if (loadState === 'ready') {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
            Your community hasn't published any additional features yet.
          </p>
        </div>
      </div>
    );
  }

  if (loadState === 'loading') {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-10 pb-safe-or-6">
          <div className="flex flex-col items-center justify-center rounded-lg bg-sunken px-6 py-12 text-center">
            <LoaderCircle className="h-7 w-7 animate-spin text-primary-700" strokeWidth={1.75} />
            <p className="mt-4 font-display text-display-sm text-ink">Loading estate features</p>
            <p className="mt-2 max-w-sm text-body-sm text-ink-secondary">
              We&apos;re checking which sections your community has published for this app.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <ErrorState
            title="Couldn't load estate features."
            description="We couldn't confirm which tiles this community has enabled, so nothing extra is shown right now."
            onRetry={retryLoad}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper">
      {header}
      <div className="px-5 pt-5 pb-safe-or-6">
        <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
          Estate features will appear here once your community configuration loads.
        </p>
      </div>
    </div>
  );
}
